import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Spinner, Table } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./AdminLogs.module.css";

interface Entry {
  readonly id: string;
  readonly orgId: string;
  readonly field: string;
  readonly oldValue: string | null;
  readonly newValue: string | null;
  readonly source: string;
  readonly reason: string | null;
  readonly createdAt: string;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; entries: readonly Entry[] }
  | { kind: "error"; message: string };

const SOURCE_OPTIONS = ["all", "webhook", "admin", "signup", "cron", "system", "membership"] as const;
const FIELD_OPTIONS = [
  "all",
  "plan",
  "subscription_status",
  "cleaner_count",
  "trial_ends_at",
  "stripe_customer_id",
  "stripe_subscription_id",
  "suspended_at",
  "suspension_reason",
] as const;

export function AdminBillingAuditPage() {
  usePageTitle("Admin · Billing audit");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [source, setSource] = useState<(typeof SOURCE_OPTIONS)[number]>("all");
  const [field, setField] = useState<(typeof FIELD_OPTIONS)[number]>("all");

  useEffect(() => {
    const params = new URLSearchParams();
    if (source !== "all") params.set("source", source);
    if (field !== "all") params.set("field", field);
    fetch(`/api/admin/billing-audit?${params.toString()}`)
      .then(async (res) => {
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as { success: boolean; data?: { entries: Entry[] } };
        if (json.success && json.data) setState({ kind: "ready", entries: json.data.entries });
        else setState({ kind: "error", message: "Unexpected response" });
      })
      .catch(() => setState({ kind: "error", message: "Network error" }));
  }, [source, field]);

  if (state.kind === "unauthorized") return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;

  return (
    <AdminPage title="Billing audit">
      <div className={styles.filters}>
        <label className={styles.field}>
          Source
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as (typeof SOURCE_OPTIONS)[number])}
            className={styles.input}
          >
            {SOURCE_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
          </select>
        </label>
        <label className={styles.field}>
          Field
          <select
            value={field}
            onChange={(e) => setField(e.target.value as (typeof FIELD_OPTIONS)[number])}
            className={styles.input}
          >
            {FIELD_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
          </select>
        </label>
      </div>

      {state.kind === "loading" ? <Spinner />
      : state.kind === "error" ? <div className={styles.empty}>{state.message}</div>
      : state.entries.length === 0 ? <div className={styles.empty}>No entries.</div>
      : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>When</Table.HeaderCell>
              <Table.HeaderCell>Org</Table.HeaderCell>
              <Table.HeaderCell>Field</Table.HeaderCell>
              <Table.HeaderCell>From</Table.HeaderCell>
              <Table.HeaderCell>To</Table.HeaderCell>
              <Table.HeaderCell>Source</Table.HeaderCell>
              <Table.HeaderCell>Reason</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.entries.map((e) => (
              <Table.Row key={e.id}>
                <Table.Cell>{new Date(e.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell><Link to={`/admin/orgs/${e.orgId}`} className={styles.link}>{e.orgId.slice(0, 8)}…</Link></Table.Cell>
                <Table.Cell>{e.field}</Table.Cell>
                <Table.Cell>{e.oldValue ?? "—"}</Table.Cell>
                <Table.Cell>{e.newValue ?? "—"}</Table.Cell>
                <Table.Cell>{e.source}</Table.Cell>
                <Table.Cell>{e.reason ?? ""}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </AdminPage>
  );
}
